// Session storage utility functions for better data persistence

// Session storage keys
export const SESSION_KEYS = {
  // Auth related
  AUTH_TOKEN: 'authToken',
  
  // User related
  USER_DATA: 'userData',
  
  // Company related
  SELECTED_COMPANY: 'selectedCompany',
  COMPANIES: 'companies',
  
  // UI state related
  UI_PREFERENCES: 'uiPreferences',
  LAST_VISITED_PAGE: 'lastVisitedPage',
} as const;

// Helper functions for session storage
export const saveToSessionStorage = (key: string, data: any): void => {
  try {
    sessionStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error(`Error saving to session storage (${key}):`, error);
  }
};

export const loadFromSessionStorage = <T = any>(key: string): T | null => {
  try {
    const data = sessionStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error(`Error loading from session storage (${key}):`, error);
    return null;
  }
};

export const removeFromSessionStorage = (key: string): void => {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.error(`Error removing from session storage (${key}):`, error);
  }
};

export const clearSessionStorage = (keys: string[]): void => {
  keys.forEach(key => {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.error(`Error clearing session storage (${key}):`, error);
    }
  });
};

export const clearAllSessionStorage = (): void => {
  try {
    sessionStorage.clear();
  } catch (error) {
    console.error('Error clearing all session storage:', error);
  }
};

// Token-specific functions (no JSON serialization for tokens)
export const saveAuthToken = (token: string): void => {
  try {
    sessionStorage.setItem(SESSION_KEYS.AUTH_TOKEN, token);
  } catch (error) {
    console.error('Error saving auth token to session storage:', error);
  }
};

export const loadAuthToken = (): string | null => {
  try {
    return sessionStorage.getItem(SESSION_KEYS.AUTH_TOKEN);
  } catch (error) {
    console.error('Error loading auth token from session storage:', error);
    return null;
  }
};

export const removeAuthToken = (): void => {
  try {
    sessionStorage.removeItem(SESSION_KEYS.AUTH_TOKEN);
  } catch (error) {
    console.error('Error removing auth token from session storage:', error);
  }
};

// Specific helper functions for common use cases
export const saveUserData = (userData: any): void => {
  saveToSessionStorage(SESSION_KEYS.USER_DATA, userData);
};

export const loadUserData = <T = any>(): T | null => {
  return loadFromSessionStorage<T>(SESSION_KEYS.USER_DATA);
};

export const saveSelectedCompany = (company: any): void => {
  saveToSessionStorage(SESSION_KEYS.SELECTED_COMPANY, company);
};

export const loadSelectedCompany = <T = any>(): T | null => {
  return loadFromSessionStorage<T>(SESSION_KEYS.SELECTED_COMPANY);
};

export const saveCompanies = (companies: any[]): void => {
  saveToSessionStorage(SESSION_KEYS.COMPANIES, companies);
};

export const loadCompanies = <T = any>(): T[] | null => {
  return loadFromSessionStorage<T[]>(SESSION_KEYS.COMPANIES);
};

export const saveUIPreferences = (preferences: any): void => {
  saveToSessionStorage(SESSION_KEYS.UI_PREFERENCES, preferences);
};

export const loadUIPreferences = <T = any>(): T | null => {
  return loadFromSessionStorage<T>(SESSION_KEYS.UI_PREFERENCES);
};

export const saveLastVisitedPage = (page: string): void => {
  saveToSessionStorage(SESSION_KEYS.LAST_VISITED_PAGE, page);
};

export const loadLastVisitedPage = (): string | null => {
  return loadFromSessionStorage<string>(SESSION_KEYS.LAST_VISITED_PAGE);
};

// Clear all application data (useful for logout)
export const clearApplicationData = (): void => {
  clearSessionStorage([
    SESSION_KEYS.USER_DATA,
    SESSION_KEYS.SELECTED_COMPANY,
    SESSION_KEYS.COMPANIES,
    SESSION_KEYS.UI_PREFERENCES,
    SESSION_KEYS.LAST_VISITED_PAGE,
  ]);
  // Note: We don't clear AUTH_TOKEN here as it's handled by AuthContext
  // AUTH_TOKEN is cleared separately using removeAuthToken()
};

// Check if session storage is available
export const isSessionStorageAvailable = (): boolean => {
  try {
    const test = '__sessionStorage_test__';
    sessionStorage.setItem(test, test);
    sessionStorage.removeItem(test);
    return true;
  } catch (error) {
    return false;
  }
};

// Debug function to check token storage
export const debugTokenStorage = (): void => {
  try {
    const token = loadAuthToken();
    console.log('Current auth token:', token);
    console.log('Token type:', typeof token);
    console.log('Token length:', token?.length);
    if (token) {
      console.log('Token starts with Bearer:', token.startsWith('Bearer'));
      console.log('Token has quotes:', token.startsWith('"') && token.endsWith('"'));
    }
  } catch (error) {
    console.error('Error debugging token storage:', error);
  }
};
